import { urlify } from '../utils/string';

export default (state = {}, action = {}) => {
	if (action.type === 'PRODUCT_CACHE') {
		// Create lookup tables.
		// Sorting done dynamically.

		// Name - Search
		const cache = action.productCache.content;
		const { languages, categories, categoryTitles, categoryTitlesLocalized } = action.productCache; // Languages WP setting not Centra.
		const filterArrays = {
			categories, // Object mapping category paths to arrays of product keys.
			colors: {}, // Object mapping color names to arrays of product keys.
			brands: {}, // Object mapping brands to arrays of product keys.
			sizes: {}, // Object mapping sizes to arrays of product keys.
			productsOnSale: [], // Array of product keys
			newProducts: [], // Array of product keys
		};
		const filterDescriptions = {
			categories: categoryTitles,
			colors: {},
			brands: {},
			sizes: {},
		};
		const languageKeys = Object.keys(languages);
		const searchTerms = languageKeys.reduce((acc, language) => ({ ...acc, [language]: {} }), {});

		const localized = languageKeys.slice(1).reduce((acc, language) => ({
			...acc,
			[language]: {
				categories: categoryTitlesLocalized ? categoryTitlesLocalized[language] : {},
				colors: {},
			},
		}), {}); // Merged localized version of above.

		/*
			Localized versions shouldn't create new arrays.
			Their arrays exist but at their non-translated key.
			How to map their keys with their localized versions.
		*/

		Object.keys(cache).forEach((cacheKey) => {
			const item = cache[cacheKey];
			if (item.productHidden) return;

			// Category lookup
			// Categories straight from Centra

			// Colors lookup
			if (item.color) {
				Object.keys(item.color).forEach((colorKey) => {
					const colorItem = item.color[colorKey];
					const colorItemUrlified = urlify(colorItem);

					if (!filterArrays.colors[colorItemUrlified]) {
						filterArrays.colors[colorItemUrlified] = [];
						filterDescriptions.colors[colorItemUrlified] = colorItem;
					}
					filterArrays.colors[colorItemUrlified][filterArrays.colors[colorItemUrlified].length] = cacheKey;
				});
			}

			// Search Terms
			searchTerms[languageKeys[0]][item.name] = searchTerms[languageKeys[0]][item.name] ? [...searchTerms[languageKeys[0]][item.name], cacheKey] : [cacheKey];

			if (languageKeys.length > 1) {
				languageKeys.slice(1).forEach((language) => {
					const isoLanguageCode = language;
					const localizedItem = item.localized ? item.localized[isoLanguageCode] : false;

					// Can't create localized versions if default doesn't exist.
					if (localizedItem && localizedItem.color) {
						Object.keys(localizedItem.color).forEach((colorKey) => {
							// Color key should exist on default as impossible to create localized version without default.
							localized[isoLanguageCode].colors[urlify(item.color[colorKey])] = {
								key: urlify(localizedItem.color[colorKey]),
								description: localizedItem.color[colorKey],
							};
						});
					}

					if (localizedItem && localizedItem.name) {
						searchTerms[isoLanguageCode][localizedItem.name] = searchTerms[isoLanguageCode][localizedItem.name] ? [...searchTerms[isoLanguageCode][localizedItem.name], cacheKey] : [cacheKey];
					} else {
						searchTerms[isoLanguageCode][item.name] = searchTerms[isoLanguageCode][item.name] ? [...searchTerms[isoLanguageCode][item.name], cacheKey] : [cacheKey];
					}
				});
			}

			// Brands lookup
			if (!filterArrays.brands[item.brandUri]) {
				filterArrays.brands[item.brandUri] = [];
				filterDescriptions.brands[item.brandUri] = item.brand;
			}
			filterArrays.brands[item.brandUri][filterArrays.brands[item.brandUri].length] = cacheKey;

			// Sizes lookup
			Object.keys(item.sizes).forEach((sizeKey) => {
				const sizeItem = item.sizes[sizeKey];
				const sizeKeyUrlified = urlify(sizeItem.name);

				if (!filterArrays.sizes[sizeKeyUrlified]) {
					filterArrays.sizes[sizeKeyUrlified] = [];
					filterDescriptions.sizes[sizeKeyUrlified] = sizeItem.name;
				}
				if (sizeItem.stock === 0) return;
				filterArrays.sizes[sizeKeyUrlified][filterArrays.sizes[sizeKeyUrlified].length] = cacheKey;
			});

			// Sale / News
			if (item.price.showAsOnSale) filterArrays.productsOnSale[filterArrays.productsOnSale.length] = cacheKey;
			if (item.price.newProduct) filterArrays.newProducts[filterArrays.newProducts.length] = cacheKey;
		});

		/*
			Extend search terms.
		*/

		// Categories
		Object.keys(filterDescriptions.categories).forEach((category) => {
			const categorySearchTerm = searchTerms[languageKeys[0]][filterDescriptions.categories[category]];
			searchTerms[languageKeys[0]][filterDescriptions.categories[category]] = !categorySearchTerm ? filterArrays.categories[category] : [...categorySearchTerm, ...filterArrays.categories[category]];

			if (languageKeys.length > 1) {
				languageKeys.slice(1).forEach((language) => {
					if (localized[language] && localized[language].categories && localized[language].categories[category]) {
						const localizedCategorySearchTerm = searchTerms[language][localized[language].categories[category].description];
						searchTerms[language][localized[language].categories[category].description] = !localizedCategorySearchTerm ? filterArrays.categories[category] : [...localizedCategorySearchTerm, ...filterArrays.categories[category]];
					} else {
						const localizedCategorySearchTerm = searchTerms[language][filterDescriptions.categories[category]];
						searchTerms[language][filterDescriptions.categories[category]] = !localizedCategorySearchTerm ? filterArrays.categories[category] : [...localizedCategorySearchTerm, ...filterArrays.categories[category]];
					}
				});
			}
		});

		// Brands
		Object.keys(filterDescriptions.brands).forEach((brand) => {
			const brandSearchTerm = searchTerms[languageKeys[0]][filterDescriptions.brands[brand]];
			searchTerms[languageKeys[0]][filterDescriptions.brands[brand]] = !brandSearchTerm ? filterArrays.brands[brand] : [...brandSearchTerm, ...filterArrays.brands[brand]];

			if (languageKeys.length > 1) {
				languageKeys.slice(1).forEach((language) => {
					// Copy default language
					searchTerms[language][filterDescriptions.brands[brand]] = [...searchTerms[languageKeys[0]][filterDescriptions.brands[brand]]];
				});
			}
		});

		// Colors
		Object.keys(filterDescriptions.colors).forEach((color) => {
			const colorSearchTerm = searchTerms[languageKeys[0]][filterDescriptions.colors[color]];
			searchTerms[languageKeys[0]][filterDescriptions.colors[color]] = !colorSearchTerm ? filterArrays.colors[color] : [...colorSearchTerm, ...filterArrays.colors[color]];

			if (languageKeys.length > 1) {
				languageKeys.slice(1).forEach((language) => {
					if (localized[language] && localized[language].colors && localized[language].colors[color]) {
						const localizedColorSearchTerm = searchTerms[language][localized[language].colors[color].description];
						searchTerms[language][localized[language].colors[color].description] = !localizedColorSearchTerm ? filterArrays.colors[color] : [...localizedColorSearchTerm, ...filterArrays.colors[color]];
					} else {
						const localizedColorSearchTerm = searchTerms[language][filterDescriptions.colors[color]];
						searchTerms[language][filterDescriptions.colors[color]] = !localizedColorSearchTerm ? filterArrays.colors[color] : [...localizedColorSearchTerm, ...filterArrays.colors[color]];
					}
				});
			}
		});

		return {
			...state,
			[action.market]: {
				...state[action.market],
				[action.pricelist]: {
					products: cache,
					filterArrays,
					filterDescriptions,
					localized,
					searchTerms,
				},
			},
		};
	}

	return state;
};
